import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import DashboardAnalyticsSection from '@/components/DashboardAnalyticsSection';
import {
  Briefcase, Clock, FileText, BarChart3, Send,
  Timer
} from 'lucide-react';
import type { TimeEntry, Project } from '../types';

function getGreeting(name?: string) {
  const hour = new Date().getHours();
  let greeting = 'Good morning';
  if (hour >= 12 && hour < 17) greeting = 'Good afternoon';
  else if (hour >= 17) greeting = 'Good evening';
  return name ? `${greeting}, ${name}` : greeting;
}

function weekStart(offset = 0) {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) - offset * 7;
  d.setDate(diff);
  d.setHours(0,0,0,0);
  return d.toISOString().split('T')[0];
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const today = new Date().toISOString().split('T')[0];
  const weekStartDate = weekStart();
  const weekEndDate = new Date(new Date(weekStartDate).getTime() + 6 * 86400000).toISOString().split('T')[0];

  const { data: projects, loading: pLoading } = useApi<Project[]>(() => api('/projects'), []);
  const { data: todaysEntries, loading: eLoading } = useApi<TimeEntry[]>(() => api(`/time-entries?startDate=${today}&endDate=${today}`), []);
  const { data: weekEntries, loading: wLoading } = useApi<TimeEntry[]>(() => api(`/time-entries?startDate=${weekStartDate}&endDate=${weekEndDate}`), []);

  const todayHours = (todaysEntries || []).reduce((s, e) => s + (e.minutes / 60), 0);
  const weekHours = (weekEntries || []).reduce((s, e) => s + (e.minutes / 60), 0);

  const recentEntries = (weekEntries || []).slice(0, 5);

  return (
    <div className="space-y-6 animate-in">
      <PageHeader title={getGreeting(user?.name)} description={new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} />

      {/* Welcome / Quick Actions */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-[var(--color-primary)] bg-gradient-to-br from-white to-slate-50">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase tracking-wider">Quick Actions</CardDescription>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <a href="/time-entries"><Timer className="h-4 w-4" /> Log Time</a>
            </Button>
            <Button variant="outline" size="sm" className="gap-2" asChild>
              <a href="/timesheets"><Send className="h-4 w-4" /> Timesheet</a>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">{t('projects')}</CardTitle>
            <Briefcase className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {pLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{projects?.length ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">{t('hoursToday') || 'Hours Today'}</CardTitle>
            <Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {eLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{todayHours.toFixed(2)}h</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Week Total</CardTitle>
            <BarChart3 className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          </CardHeader>
          <CardContent>
            {wLoading ? <Skeleton className="h-8 w-16" /> : (
              <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{weekHours.toFixed(2)}h</div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Analytics Dashboard */}
        <div className="lg:col-span-2">
          <DashboardAnalyticsSection />
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Clock className="h-4 w-4 text-[var(--color-primary)]" /> Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-2">
            {wLoading ? (
              <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div>
            ) : recentEntries.length === 0 ? (
              <EmptyState icon={FileText} title="No recent activity" description="Log your first time entry to see it here." className="py-6" />
            ) : (
              recentEntries.map((e) => (
                <div key={e.id} className="flex items-center gap-3 rounded-lg border-l-4 border-l-[var(--color-primary)] bg-slate-50 p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.project?.name}</div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">{e.task?.name} • {new Date(e.date).toLocaleDateString()}</div>
                  </div>
                  <div className="text-sm font-bold text-[var(--color-primary)] font-mono">{(e.minutes / 60).toFixed(1)}h</div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
